import { useEffect, useRef, useState } from "react";
import moment from "moment";
import * as stringConstants from "../../Constants/stringConstants";
import * as apiHelper from "../../Services/ApiHelper";
import * as commonServices from "../../Services/common";
import { changeDateFormatForServer, changeDateTimeFormatForServer } from "../../Utils/dateUtils";
import * as stringUtils from "../../Utils/stringUtils";
import * as toast from "../../Wrapper/toast/toast";
import InventoryUI from "./InventoryUI";

const Inventory = () => {
  const [modalShow, setModalShow] = useState(false);
  const [inventoryList, setInventoryList] = useState([]);
  const [dates, setDates] = useState({ startDate: null, endDate: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [lastSelectedPage, setLastSelectedPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importList, setImportList] = useState();
  const [selectedRows, setSelectedRows] = useState([]);

  const datePickerStartRef = useRef();
  const datePickerEndRef = useRef();

  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const handleClose = () => {
    setModalShow(false);
    clearData();
  };

  const handleAddStockClose = () => {
    setModalAddStockShow(false);
    clearData();
  };

  const handleAddCommentClose = () => {
    setModalAddCommentShow(false);
    clearData();
  };

  const handleHistoryClose = () => {
    setModalHistoryShow(false);
    clearData();
  };

  const handleAddConsumeClose = () => {
    setModalAddConsumeShow(false);
    clearData();
  };

  const initialAddInventoryData = {
    name: "",
    description: "",
    action: "Add",
    id: null,
    status: "",
    inventory_category_id: null,
    expiry_date: null,
  };

  const initialAddStockData = {
    amount: 0,
    qty: null,
    date: null,
    expiry_date: null,
    id: null,
    remarks: "",
  };

  const initialAddConsumeData = {
    qty: null,
    id: null,
    remarks: "",
    patient_id: null,
    date: moment().format("YYYY-MM-DDTHH:mm"),
  };

  const initialAddComment = {
    id: null,
    comment: "",
  };

  const initialHistory = {
    id: null,
    name: "",
  };

  const [addInventoryData, setAddInventoryData] = useState(initialAddInventoryData);

  const [addInventoryStockData, setAddInventoryStockData] = useState(initialAddStockData);
  const [modalAddStockShow, setModalAddStockShow] = useState(false);

  const [addInventoryConsumeData, setAddInventoryConsumeData] = useState(initialAddConsumeData);
  const [modalAddConsumeShow, setModalAddConsumeShow] = useState(false);

  const [addInventoryComment, setAddInventoryComment] = useState(initialAddComment);
  const [modalAddCommentShow, setModalAddCommentShow] = useState(false);

  const [addInventoryHistory, setAddInventoryHistory] = useState([]);
  const [historyData, setHistoryData] = useState(initialHistory);
  const [modalHistoryShow, setModalHistoryShow] = useState(false);

  useEffect(() => {
    if (
      (dates.startDate !== null && dates.endDate !== null) ||
      (dates.startDate === null && dates.endDate === null)
    ) {
      getInventoryList(lastSelectedPage);
    }
  }, [dates, selectedStatus, selectedTypes]);

  const onPageChange = (page) => {
    getInventoryList(page);
  };

  const getInventoryList = (page = 1) => {
    let query = `?page=${page}&limit=10&start_date=${changeDateFormatForServer(dates.startDate)}&end_date=${changeDateFormatForServer(dates.endDate)}&q=${searchQuery}`;
    selectedStatus.forEach((s, i) => {
      query += `&status[${i}]=${s}`;
    });
    selectedTypes.forEach((s, i) => {
      query += `&category[${i}]=${s}`;
    });
    setLoading(true);
    setLastSelectedPage(page);
    apiHelper
      .getInventoryList(query)
      .then((response) => {
        if (response?.isSuccess) {
          setInventoryList(response?.data);
        } else {
          toast.warn(response?.message);
        }
      })
      .catch((err) => toast.warn(err))
      .finally(() => setLoading(false));
  };

  const clearData = () => {
    setAddInventoryData(initialAddInventoryData);
    setAddInventoryStockData(initialAddStockData);
    setAddInventoryConsumeData(initialAddConsumeData);
    setAddInventoryComment(initialAddComment);
    setAddInventoryHistory(initialHistory);
  };

  const validateData = () => {
    const { name, inventory_category_id } = addInventoryData;
    let validData = true;

    if (stringUtils.isNullOrEmpty(name)) {
      toast.warn(stringConstants.INVENTORY_NAME_REQUIRED);
      validData = false;
    } else if (!stringUtils.isValidName(name)) {
      toast.warn(stringConstants.INVENTORY_LENGTH);
      validData = false;
    } else if (stringUtils.isNullOrEmpty(inventory_category_id)) {
      toast.warn(stringConstants.CATEGORY_REQUIRED);
      validData = false;
    }

    return validData;
  };

  const validateStockData = () => {
    const { amount, qty, date } = addInventoryStockData;

    let validData = true;

    if (stringUtils.isNullOrEmpty(qty)) {
      toast.warn(stringConstants.QUANTITY_REQUIRED);
      validData = false;
    } else if (stringUtils.isNullOrEmpty(date)) {
      toast.warn(stringConstants.DATE_REQUIRED);
      validData = false;
    }

    return validData;
  };

  const validateConsumeData = () => {
    const { qty, stock } = addInventoryConsumeData;

    let validData = true;

    if (stock === null || stock <= 0) {
      toast.warn("Please add stock to consume.");
      validData = false;
    } else if (stringUtils.isNullOrEmpty(qty)) {
      toast.warn(stringConstants.QUANTITY_REQUIRED);
      validData = false;
    } else if (qty > stock) {
      toast.warn("Quantity must be less than " + stock);
      validData = false;
    }

    return validData;
  };

  const validateCommentData = () => {
    const { comment } = addInventoryComment;

    let validData = true;

    if (stringUtils.isNullOrEmpty(comment)) {
      toast.warn(stringConstants.COMMENT_REQUIRED);
      validData = false;
    }

    return validData;
  };

  const addInventory = () => {
    if (validateData()) {
      setButtonLoading(true);
      const { name, remarks, inventory_category_id, expiry_date } = addInventoryData;
      apiHelper
        .addInventory({
          name,
          remarks,
          inventory_category_id,
          expiry_date: changeDateFormatForServer(expiry_date),
        })
        .then((response) => {
          setButtonLoading(false);
          if (response?.isSuccess) {
            toast.success(response.message);
            getInventoryList(lastSelectedPage);
            commonServices.fetchSystemData();

            setModalShow(false);
            clearData();
          } else {
            toast.warn(response?.message);
          }
        })
        .catch((error) => {
          toast.warn(error);
        })
        .finally(() => {
          setButtonLoading(false);
        });
    }
  };

  const addInventoryStock = () => {
    if (validateStockData()) {
      const { amount, qty, date, remarks, id, expiry_date } = addInventoryStockData;
      setButtonLoading(true);
      apiHelper
        .addInventoryStock({
          id,
          price: !stringUtils.isNullOrEmpty(amount) ? amount : 0,
          qty,
          date: changeDateFormatForServer(date),
          expiry_date: changeDateFormatForServer(expiry_date),
          remarks,
        })
        .then((response) => {
          setButtonLoading(false);
          if (response?.isSuccess) {
            getInventoryList(lastSelectedPage);
            commonServices.fetchSystemData();
            toast.success(response.message);
            setModalAddStockShow(false);
            clearData();
          } else {
            toast.warn(response?.message);
          }
        })
        .catch((error) => {
          toast.warn(error);
        })
        .finally(() => {
          setButtonLoading(false);
        });
    }
  };

  const addInventoryConsume = () => {
    if (validateConsumeData()) {
      const { qty, remarks, id, patient_id, date } = addInventoryConsumeData;
      setButtonLoading(true);
      apiHelper
        .addInventoryConsume({
          id,
          qty,
          remarks,
          patient_id,
          date: changeDateTimeFormatForServer(date),
        })
        .then((response) => {
          setButtonLoading(false);
          if (response?.isSuccess) {
            getInventoryList(lastSelectedPage);
            commonServices.fetchSystemData();
            toast.success(response.message);
            setModalAddConsumeShow(false);
            clearData();
          } else {
            toast.warn(response?.message);
          }
        })
        .catch((error) => {
          toast.warn(error);
        })
        .finally(() => {
          setButtonLoading(false);
        });
    }
  };

  const addInventoryComments = () => {
    if (validateCommentData()) {
      const { comment, id } = addInventoryComment;
      setButtonLoading(true);
      apiHelper
        .addInventoryComment({ id, comment })
        .then((response) => {
          setButtonLoading(false);
          if (response?.isSuccess) {
            getInventoryList(lastSelectedPage);
            commonServices.fetchSystemData();
            toast.success(response.message);
            setModalAddCommentShow(false);
            clearData();
          } else {
            toast.warn(response?.message);
          }
        })
        .catch((error) => {
          toast.warn(error);
        })
        .finally(() => {
          setButtonLoading(false);
        });
    }
  };

  const Action = (data, action = "") => {
    if (action === "edit") {
      let categories = commonServices.removeInActive(commonServices.getInventoryCategories());
      const exists = categories.some((obj) => obj.value === data.inventory_category_id);

      setAddInventoryData({
        name: data.name,
        remarks: data.remarks,
        action: "Edit",
        id: data.id,
        inventory_category_id: exists ? data.inventory_category_id : null,
        expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
      });

      setModalShow(true);
    } else if (action === "addStock") {
      setAddInventoryStockData({
        ...addInventoryStockData,
        id: data.id,
        expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
      });
      setModalAddStockShow(true);
    } else if (action === "addConsume") {
      setAddInventoryConsumeData({
        ...addInventoryConsumeData,
        id: data.id,
        name: data.name,
        stock: data.stock,
      });
      setModalAddConsumeShow(true);
    } else if (action === "addComment") {
      setAddInventoryComment({
        ...addInventoryComment,
        id: data.id,
        name: data.name,
      });
      setModalAddCommentShow(true);
    } else if (action === "history") {
      setHistoryData({ id: data.id, name: data.name });
      setLoading(true);
      apiHelper
        .getInventoryHistory("?id=" + data?.id + "&page=1")
        .then((response) => {
          setLoading(false);
          if (response?.isSuccess) {
            setAddInventoryHistory(response?.data);
            // toast.success(response.message);
            setModalHistoryShow(true);
          } else {
            toast.warn(response?.message);
          }
        })
        .catch((error) => {
          toast.warn(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const editInventory = (id) => {
    if (validateData()) {
      setButtonLoading(true);
      const { name, remarks, inventory_category_id, expiry_date } = addInventoryData;
      apiHelper
        .editInventory({
          id,
          name,
          remarks,
          inventory_category_id,
          expiry_date: changeDateFormatForServer(expiry_date),
        })
        .then((response) => {
        setButtonLoading(false);
        if (response?.isSuccess) {
          getInventoryList(lastSelectedPage);
          commonServices.fetchSystemData();
          toast.success(response.message);
          setModalShow(false);
          clearData();
        } else {
          toast.warn(response?.message);
        }
      });
    }
  };
  const importListing = (data) => {
    if (importModal) {
      setLoading(true);
      apiHelper
        .importMaster("inventory_type", data)
        .then((response) => {
          if (response?.isSuccess) {
            setImportList(response?.data);
          }
        })
        .catch((err) => toast.warn(err))
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const openImportModal = () => {
    setImportModal(true);
  };

  const exportList = () => {
    setButtonLoading(true);
    apiHelper
      .exportMaster("inventory_type", { ids: selectedRows })
      .then((response) => {
        if (response?.isSuccess) {
          toast.success(response?.message);
          getInventoryList(lastSelectedPage);
          commonServices.fetchSystemData(setLoading);
        } else {
          toast.warn(response.message);
        }
      })
      .catch((err) => toast.warn(err))
      .finally(() => {
        setButtonLoading(false);
        setImportModal(false);
      });
  };

  const statusHandler = (data) => {
    const updatedStatus = data.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setAddInventoryData({ ...addInventoryData, status: updatedStatus });
    setButtonLoading(true);

    apiHelper.changeInventoryStatus(data.id, { status: updatedStatus }).then((response) => {
      setButtonLoading(false);
      if (response?.isSuccess) {
        getInventoryList(lastSelectedPage);
        commonServices.fetchSystemData();
        toast.success(response.message);
        setModalShow(false);
        clearData();
      } else {
        toast.warn(response?.message);
      }
    });
  };

  const handleHistoryPageChange = (page) => {
    setLoading(true);
    apiHelper
      .getInventoryHistory("?id=" + historyData?.id + "&page=" + page)
      .then((response) => {
        setLoading(false);
        if (response?.isSuccess) {
          setAddInventoryHistory(response?.data);
          // toast.success(response.message);
          setModalHistoryShow(true);
        } else {
          toast.warn(response?.message);
        }
      })
      .catch((error) => {
        toast.warn(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTypeDropdownChange = (options) => {
    setSelectedTypes(options);
  };

  const handleStatusDropdownChange = (options) => {
    setSelectedStatus(options);
  };

  return (
    <>
      <InventoryUI
        modalShow={modalShow}
        setModalShow={setModalShow}
        handleClose={handleClose}
        inventoryList={inventoryList}
        addInventoryData={addInventoryData}
        setAddInventoryData={setAddInventoryData}
        dates={dates}
        setDates={setDates}
        datePickerStartRef={datePickerStartRef}
        datePickerEndRef={datePickerEndRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        addInventory={addInventory}
        onPageChange={onPageChange}
        editInventory={editInventory}
        loading={loading}
        buttonLoading={buttonLoading}
        Action={Action}
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        getInventoryList={getInventoryList}
        statusHandler={statusHandler}
        importListing={importListing}
        exportList={exportList}
        importModal={importModal}
        importList={importList}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        setImportModal={setImportModal}
        openImportModal={openImportModal}
        modalAddStockShow={modalAddStockShow}
        setModalAddStockShow={setModalAddStockShow}
        addInventoryStockData={addInventoryStockData}
        setAddInventoryStockData={setAddInventoryStockData}
        handleAddStockClose={handleAddStockClose}
        addInventoryStock={addInventoryStock}
        modalAddConsumeShow={modalAddConsumeShow}
        setModalAddConsumeShow={setModalAddConsumeShow}
        addInventoryConsumeData={addInventoryConsumeData}
        setAddInventoryConsumeData={setAddInventoryConsumeData}
        handleAddConsumeClose={handleAddConsumeClose}
        addInventoryConsume={addInventoryConsume}
        modalAddCommentShow={modalAddCommentShow}
        setModalAddCommentShow={setModalAddCommentShow}
        addInventoryComment={addInventoryComment}
        setAddInventoryComment={setAddInventoryComment}
        handleAddCommentClose={handleAddCommentClose}
        addInventoryComments={addInventoryComments}
        modalHistoryShow={modalHistoryShow}
        setModalHistoryShow={setModalHistoryShow}
        addInventoryHistory={addInventoryHistory}
        setAddInventoryHistory={setAddInventoryHistory}
        handleHistoryClose={handleHistoryClose}
        handleHistoryPageChange={handleHistoryPageChange}
        selectedTypes={selectedTypes}
        setSelectedTypes={setSelectedTypes}
        handleTypeDropdownChange={handleTypeDropdownChange}
        handleStatusDropdownChange={handleStatusDropdownChange}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        historyData={historyData}
      />
    </>
  );
};

export default Inventory;
